import "./paymentsMetrics.scss";
import { ArTooltip } from "./ArTooltip";
import { Card, Carousel, Flex, Space, Statistic } from "antd";
import { type FormattedMetric, useTransactionMetrics } from "api/clients/useTransactionMetrics";
import { PaymentMetricsPending } from "./PaymentsMetricsPending";
import { PaymentsMetricsChange } from "./PaymentsMetricsChange";
import { PaymentsMetricsNoDataNotice } from "./PaymentsMetricsNoDataNotice";
import { differenceInTimeUnits } from "@vyne-shared/utils/date";
import { getClassName, getWrapperClassName } from "./paymentsMetricsHelpers";
import { useAuth } from "auth/useAuth";
import { useHeaderDates } from "molecules/filters/rangePicker/datePickerContext";
import { useMasonFlag } from "utils/useMasonFlag";
import type { LoginResponse } from "api/responses";

const AccountsBalance = "Total Accounts Balance";
const Requests = "Requests";
const AmountRequested = "Amount Requested";
const Payments = "Payments";
const AmountCollected = "Amount Collected";

/** Separator for complex metric data. */
const sep = "|";

// accounts receivable metrics
const arMetrics = {
    ZeroToThirty: { title: "0-30 Days", className: "zero-to-thirty" },
    ThirtyToSixty: { title: "31-60 Days", className: "thirty-to-sixty" },
    SixtyToNinety: { title: "61-90 Days", className: "sixty-to-ninety" },
    NinetyPlus: { title: "90+ Days", className: "ninety-plus" },
};

const arMetricsKeys = Object.freeze(Object.keys(arMetrics)) as readonly (keyof typeof arMetrics)[];
const arMetricsTitles = Object.freeze(arMetricsKeys.map((key) => arMetrics[key].title));

interface PaymentsMetricsProps {
    /** Callback for when a metric is clicked. */
    readonly onMetricsClick?: (agingIndex: number | null) => void;

    /** Whether the user is on the onboarding page. */
    readonly isMarketing: boolean;
}

/** Displays key metrics from the Payments system. */
export const PaymentsMetrics = ({ onMetricsClick, isMarketing }: PaymentsMetricsProps) => {
    const [startDateMs, endDateMs] = useHeaderDates();
    const { isPending, isSuccess, isError, isPlaceholderData, data } = useTransactionMetrics(
        startDateMs,
        endDateMs,
        isMarketing,
    );
    const isIntegratedData = hasIntegratedData(data);
    const isIntegratedLogin = useMasonFlag();

    // If the user is on the marketing page and no non-zero metrics were returned, don't show the metrics.
    // Note that on the marketing page, IntegratedMetricsLoading will show a skeleton in the shape of the metrics cards,
    // even if it turns out (after data is loaded) that there are no metrics to show.
    // Also, on the marketing page, silently skip showing the metrics (don't show alert) in the event of an error.
    if (isMarketing && !isPending && !isIntegratedData) {
        return null;
    }

    // When on the marketing page, presume we're integrated. Otherwise, use the isIntegrated flag from login result.
    const isIntegrated = isMarketing || isIntegratedLogin;
    const dayDifference = Math.abs(differenceInTimeUnits(startDateMs!, endDateMs!, "day"));

    return (
        <>
            {!isSuccess ? (
                <PaymentMetricsPending
                    isError={isError}
                    isPending={isPending}
                    isIntegrated={isIntegrated}
                    isMarketing={isMarketing}
                />
            ) : (
                <Space direction="vertical" className={getWrapperClassName(false, isIntegrated, isMarketing)} size={15}>
                    {isIntegrated ? (
                        <>
                            {!isMarketing && <ArTooltip dayDifference={dayDifference} />}
                            <MetricsDisplay
                                metrics={data}
                                className={getClassName(isPlaceholderData)}
                                onMetricsClick={onMetricsClick}
                                isMarketing={isMarketing}
                                dayDifference={dayDifference}
                            />
                        </>
                    ) : (
                        <DefaultMetrics metrics={data} className={getClassName(isPlaceholderData)} />
                    )}
                </Space>
            )}
        </>
    );
};

function hasIntegratedData(data: Record<string, FormattedMetric> | undefined) {
    if (!data) return false;

    // titles are "0-30 Days" through "90+ Days"
    const metrics = arMetricsTitles.map((x) => data[x]).filter((x) => !!x);
    const nonzeros = metrics.reduce((acc, metric) => acc + (metric.isNonZero ? 1 : 0), 0);
    return nonzeros > 0;
}

function metricsHasValues(data: Record<string, FormattedMetric> | undefined) {
    // if any metric isNull is true, return false
    return !data ? false : Object.values(data).every((metric) => !metric.isNull);
}

interface CommonMetricsProps {
    readonly className: string;
    readonly metrics: Record<string, FormattedMetric>;
}

interface MetricsProps extends CommonMetricsProps, PaymentsMetricsProps {
    dayDifference: number;
}

/** Formats raw metrics into displayed cards. */
const MetricsDisplay = ({ className, metrics, onMetricsClick, isMarketing, dayDifference }: MetricsProps) => {
    const loginResult = useAuth((state) => state.loginResult);

    // whether there is non null metrics data
    const hasValues = metricsHasValues(metrics);

    // if logged in and no data yet, tell user we'll start showing once available
    const showNoDataAlert = !isMarketing && !hasValues;

    return (
        <Carousel
            arrows={!isMarketing}
            infinite={false}
            dots={false}
            className={showNoDataAlert ? "vyne-metrics-no-data" : ""}
        >
            <div>
                {isMarketing && loginResult && <MarketingMetricsHeader loginResult={loginResult} metrics={metrics} />}
                {showNoDataAlert ? (
                    <PaymentsMetricsNoDataNotice />
                ) : (
                    <ArMetrics
                        className={className}
                        metrics={metrics}
                        onMetricsClick={onMetricsClick}
                        dayDifference={dayDifference}
                        isMarketing={isMarketing}
                    />
                )}
            </div>
            {!isMarketing && (
                <DefaultMetrics metrics={metrics} className={`${className} vyne-metrics-default-carousel`} />
            )}
        </Carousel>
    );
};

const DefaultMetrics = ({ className, metrics }: CommonMetricsProps) => (
    <div className={`${className} vyne-payments-landing-metrics-default`}>
        {/* Amount Requested */}
        {metrics[AmountRequested] && metrics[Requests] && (
            <Card className="metrics-cards metrics-amount-requested">
                <Statistic
                    title={metrics[AmountRequested].title}
                    value={`${metrics[AmountRequested].value}${sep}${metrics[Requests].value} ${metrics[Requests].title}`}
                    formatter={statisticFormatter}
                />
            </Card>
        )}
        {/* Amount Collected */}
        {metrics[AmountCollected] && metrics[Payments] && (
            <Card className="metrics-cards metrics-amount-collected">
                <Statistic
                    title={metrics[AmountCollected].title}
                    value={`${metrics[AmountCollected].value}${sep}${metrics[Payments].value} ${metrics[Payments].title}`}
                    formatter={statisticFormatter}
                />
            </Card>
        )}
    </div>
);

interface MarketingMetricsHeaderProps {
    loginResult: LoginResponse;
    metrics: Record<string, FormattedMetric>;
}

const MarketingMetricsHeader = ({ loginResult, metrics }: MarketingMetricsHeaderProps) => (
    <div className="marketing-metrics-header">
        <h2>{loginResult.customerName}'s AR Breakdown</h2>
        {metrics[AccountsBalance] && metrics[arMetrics.NinetyPlus.title] && (
            <h3>
                You currently have <strong>{metrics[AccountsBalance].value} in unpaid balances</strong> - and over{" "}
                <strong>{metrics[arMetrics.NinetyPlus.title]!.value}</strong> of that is{" "}
                <strong>more than 90 days old</strong>.
            </h3>
        )}
    </div>
);

interface ArMetricsProps extends Pick<MetricsProps, "className" | "metrics" | "onMetricsClick"> {
    isMarketing: boolean;
    dayDifference: number;
}

const ArMetrics = ({ className, isMarketing, metrics, onMetricsClick, dayDifference }: ArMetricsProps) => {
    const metricsClickableClass = onMetricsClick ? "metrics-clickable" : "";

    return (
        <div className="vyne-payments-landing-metrics-carousel">
            <div className={className}>
                {/* Accounts Balance (when PMS-integrated) */}
                {metrics[AccountsBalance] && (
                    <Card
                        className={`metrics-cards metrics-total-account-balance ${metricsClickableClass}`}
                        onClick={() => onMetricsClick?.(null)}
                    >
                        <Statistic title={metrics[AccountsBalance].title} value={metrics[AccountsBalance].value} />
                        {!isMarketing && (
                            <PaymentsMetricsChange
                                percentChange={metrics[AccountsBalance].percentChange}
                                valueChange={metrics[AccountsBalance].valueChange}
                                dayDifference={dayDifference}
                            />
                        )}
                    </Card>
                )}
                {/* Map of arMetrics */}
                {arMetricsKeys.map((arKey, index) => {
                    const metric = metrics[arMetrics[arKey].title];
                    if (metric) {
                        return (
                            <Card
                                key={arKey}
                                className={`metrics-cards metrics-${arMetrics[arKey].className} ${metricsClickableClass}`}
                                onClick={() => onMetricsClick?.(index)}
                            >
                                <Statistic title={metric.title} value={metric.value} />
                                {!isMarketing && (
                                    <PaymentsMetricsChange
                                        percentChange={metric.percentChange}
                                        valueChange={metric.valueChange}
                                        dayDifference={dayDifference}
                                    />
                                )}
                            </Card>
                        );
                    }
                    return null;
                })}
            </div>
        </div>
    );
};

/** Format metric data for display. */
function statisticFormatter(value: string | number) {
    const str = value.toString();
    const parts = str.split(sep);
    if (parts.length === 1) {
        // one part
        return str;
    }

    // more than one part
    return (
        <Flex justify="center" align="center">
            <span>{parts[0]}</span>
            <span className="metrics-pipe">|</span>
            <span className="metrics-secondary">{parts[1]}</span>
        </Flex>
    );
}
